import {
    Paper,
    Typography,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Checkbox,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useContext, useMemo, useCallback } from "react";
import { SessionContext } from "../Contexts/SessionContext";
import { useTripRateContext } from "../TripRate/TripRateContext";
import CompactTable from "../Utilities/CompactTable";
import { CounterBadge } from "../Utilities/CounterBadge";
import DropDownViewModel from "../Utilities/ViewModels/DropDownViewModel";
import { getFilteredSurveys } from "./TriprateFilters";
import useData from "../Utilities/Hooks/UseData";
import Line from "../Utilities/Line";

export default function ResidentialUnitTypesFilter() {
    const { sessionState, setSessionField } = useContext(SessionContext);
    const { surveys } = useTripRateContext();

    // Selected residential type IDs from session state
    const selectedTypes: number[] = sessionState?.residentialUnitTypes ?? [];

    // 🔹 Load residential type definitions from backend (seeded table)
    const { data: baseDropdown = [] } = useData<DropDownViewModel[]>(
        "TripRateFilterNew",
        "GetResidentialUnitType"
    );


    // Surveys that remain after all current filters, including any residential
    // types that have already been excluded.
    const filteredSurveys = useMemo(() => {
        return getFilteredSurveys(
            surveys
        );
    }, [
        surveys,
        sessionState.primaryFilters,
        sessionState.secondaryFilters,
        sessionState.landusePage,
        sessionState.residentialUnitTypes,
    ]);

    const typeCounts = useMemo(() => {
        if (!baseDropdown?.length) return [];

        const keyMap = [
            "hasDetachedHouses",
            "hasSemiDetachedHouses",
            "hasTerracedHouses",
            "hasBungalows",
            "hasFlatsInHouses",
            "hasFlatsInBlocks",
            "hasTownHouses",
        ];

        return baseDropdown.map((type) => {
            const prop = keyMap[type.id];

            // For an included type, this is the number of currently eligible
            // surveys that would be removed if the type were unchecked.
            // Excluded types have no matching surveys in filteredSurveys, so
            // their count is zero.
            const count = filteredSurveys.filter((s) => s[prop] === true).length;

            return {
                id: type.id,
                label: type.label ?? "",
                count,
            };
        });
    }, [filteredSurveys, baseDropdown]);


    const handleToggle = useCallback(
        (id: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const updated = e.target.checked
                ? [...selectedTypes, id]
                : selectedTypes.filter((x) => x !== id);

            setSessionField("residentialUnitTypes", updated);
        },
        [selectedTypes, setSessionField]
    );

    return (
        <Grid container size={12} component={Paper} p={2} sx={{ position: 'relative' }}>
            <CounterBadge value={'7'} />
            <Grid container size={12} alignItems="center" justifyContent="center" sx={{ position: "relative" }}>
                <Typography textAlign="center">
                    Residential Unit Types
                </Typography>
            </Grid>
            <Line />
            <Grid size={12}>
                <TableContainer>
                    <CompactTable>
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell>Count</TableCell>
                                <TableCell>Include</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {typeCounts.map((t) => (
                                <TableRow key={t.id}>
                                    <TableCell>{t.label}</TableCell>
                                    <TableCell>{t.count}</TableCell>
                                    <TableCell>
                                        <Checkbox
                                            checked={selectedTypes.includes(t.id)}
                                            onChange={handleToggle(t.id)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </CompactTable>
                </TableContainer>
            </Grid>
        </Grid>
    );
}
